import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import { AppConfig } from '../config/app.config';
import { MsalOptionalGuard } from './guards/msal-optional.guard';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string): void {
  console.log(message);
}

export function MSALInstanceFactory(
  appConfig: AppConfig
): IPublicClientApplication {
  const azureAD = appConfig.getAzureAD();
  return new PublicClientApplication({
    auth: {
      clientId: azureAD?.auth.clientId,
      authority: azureAD?.auth.authority,
      redirectUri: azureAD?.auth.redirectUri,
      postLogoutRedirectUri: azureAD?.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      loggerOptions: {
        loggerCallback: azureAD.enabled ? loggerCallback : () => {},
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(
  appConfig: AppConfig
): MsalInterceptorConfiguration {
  const azureAD = appConfig.getAzureAD();
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(azureAD.resourceMap.uri, azureAD.resourceMap.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}

@NgModule({
  declarations: [],
  imports: [CommonModule, MsalModule],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppConfig],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [AppConfig],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MsalOptionalGuard,
  ],
})
export class AzureADModule {
  constructor(@Optional() @SkipSelf() parentModule: AzureADModule) {
    if (parentModule) {
      throw new Error(
        'Sigv library AzureADModule is already loaded. Import it in the AppModule only'
      );
    }
  }
}
